import { useQuery } from "@tanstack/react-query";
import { Button } from "@veil/ui/components/button";
import { cn } from "@veil/ui/lib/utils";
import Check from "lucide-react/dist/esm/icons/check";
import Copy from "lucide-react/dist/esm/icons/copy";
import Download from "lucide-react/dist/esm/icons/download";
import FileBadge from "lucide-react/dist/esm/icons/file-badge";
import { useState } from "react";
import { toast } from "sonner";
import { getTlsCertificate } from "@/pages/setup/tls-api";

export const TLS_CERTIFICATE_KEY = ["tls-certificate"] as const;

/** Map the backend cert `source` to a short human label. */
function sourceLabel(source: string): string {
	switch (source) {
		case "byo":
			return "Custom certificate";
		case "origin-ca":
			return "Cloudflare Origin CA";
		case "self-signed":
			return "Self-signed (origin)";
		case "acme":
			return "Let's Encrypt";
		default:
			return source || "Origin certificate";
	}
}

function formatExpiry(notAfter: string): string | null {
	const ts = Date.parse(notAfter);
	if (Number.isNaN(ts)) return null;
	return new Date(ts).toLocaleDateString(undefined, {
		year: "numeric",
		month: "short",
		day: "numeric",
	});
}

/**
 * Active-origin-certificate download affordance. Fetches the current origin
 * cert (`GET /api/system/tls/certificate`) and, when one exists, surfaces its
 * source + fingerprint + expiry with copy / download actions (PEM only — the
 * private key is never exposed).
 *
 * Renders nothing when there is no origin cert (404 → cloudflare-flexible has
 * none) or while the caller has it disabled. Pass `enabled={false}` to skip
 * the query entirely (e.g. the currently-selected mode is cloudflare-flexible).
 */
export function TlsCertDownload({
	enabled = true,
	className,
}: {
	enabled?: boolean;
	className?: string;
}) {
	const [copied, setCopied] = useState(false);
	const query = useQuery({
		queryKey: TLS_CERTIFICATE_KEY,
		queryFn: getTlsCertificate,
		enabled,
		retry: false,
		staleTime: 30_000,
	});

	const cert = query.data;
	// No origin cert (flexible / not-yet-issued) or still loading: render nothing.
	if (!enabled || !cert || !cert.pem) return null;

	const expiry = formatExpiry(cert.notAfter);

	const onCopy = async () => {
		try {
			await navigator.clipboard.writeText(cert.pem);
			setCopied(true);
			setTimeout(() => setCopied(false), 1500);
		} catch {
			toast.error("Couldn't copy to clipboard");
		}
	};

	const onDownload = () => {
		const blob = new Blob([cert.pem], { type: "application/x-pem-file" });
		const url = URL.createObjectURL(blob);
		const a = document.createElement("a");
		a.href = url;
		a.download = "origin-certificate.pem";
		document.body.appendChild(a);
		a.click();
		a.remove();
		URL.revokeObjectURL(url);
	};

	return (
		<div
			className={cn(
				"rounded-lg border border-border/60 bg-background/60 p-3.5",
				className,
			)}
		>
			<div className="flex items-start gap-3">
				<FileBadge className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
				<div className="min-w-0 flex-1">
					<p className="text-sm font-medium">Origin certificate</p>
					<dl className="mt-1.5 grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-1 text-xs">
						<dt className="text-muted-foreground">Source</dt>
						<dd className="text-foreground">{sourceLabel(cert.source)}</dd>
						{cert.fingerprint && (
							<>
								<dt className="text-muted-foreground">Fingerprint</dt>
								<dd className="truncate font-mono text-[0.7rem] text-foreground">
									{cert.fingerprint}
								</dd>
							</>
						)}
						{expiry && (
							<>
								<dt className="text-muted-foreground">Expires</dt>
								<dd className="text-foreground">{expiry}</dd>
							</>
						)}
					</dl>
					<div className="mt-3 flex flex-wrap gap-2">
						<Button size="sm" variant="outline" onClick={onDownload}>
							<Download data-icon="inline-start" />
							Download certificate
						</Button>
						<Button size="sm" variant="ghost" onClick={onCopy}>
							{copied ? (
								<Check data-icon="inline-start" />
							) : (
								<Copy data-icon="inline-start" />
							)}
							{copied ? "Copied" : "Copy PEM"}
						</Button>
					</div>
				</div>
			</div>
		</div>
	);
}
